import { z } from 'zod';
import { xmlValueSchema } from '../xml-value-schema';
import { msatsCombinedStructuredAddressSchema } from '../msat-combined-structured-address-schema';
import { FullPropertyAddressSchema } from '../full-property-address-schema';
import { FlatType, State, StreetType } from '@energy-retailer/constants';
import { houseNumberParser, suffixParser } from '../utils/address-parser';

export const msatsAddressSchema = z
  .object({
    StructuredAddress: z
      .object({
        FlatOrUnit: z
          .object({
            FlatOrUnitType: xmlValueSchema.transform(
              (value) => value as FlatType
            ),
            FlatOrUnitNumber: xmlValueSchema,
          })
          .partial(),
        House: z
          .object({
            HouseNumber: xmlValueSchema,
            HouseNumberSuffix: xmlValueSchema,
            HouseNumberTo: xmlValueSchema,
            HouseNumberToSuffix: xmlValueSchema,
          })
          .partial(),
        Street: z
          .object({
            StreetName: xmlValueSchema,
            StreetType: xmlValueSchema.transform(
              (value) => value as StreetType
            ),
          })
          .partial(),
      })
      .partial(),
    PostCode: xmlValueSchema,
    StateOrTerritory: xmlValueSchema.transform((value) => value as State),
    SuburbOrPlaceOrLocality: xmlValueSchema,
  })
  .transform((value): Omit<FullPropertyAddressSchema, 'id'> => {
    const { houseNumber, houseSuffix } = houseNumberParser(
      value.StructuredAddress.House?.HouseNumber || ''
    );

    const { houseNumber: houseNumberTo, houseSuffix: houseNumberToSuffix } =
      houseNumberParser(value.StructuredAddress.House?.HouseNumberTo || '');

    let result = {
      full: msatsCombinedStructuredAddressSchema.parse(value),
      houseNumber,
      houseNumberTo,
      houseSuffix:
        houseSuffix || value.StructuredAddress.House?.HouseNumberSuffix,
      houseNumberToSuffix:
        houseNumberToSuffix ||
        value.StructuredAddress.House?.HouseNumberToSuffix,
      country: 'AUSTRALIA',
      streetName: value.StructuredAddress.Street!.StreetName!,
      streetType: value.StructuredAddress.Street!.StreetType!,
      flatNumber: value.StructuredAddress.FlatOrUnit?.FlatOrUnitNumber,
      flatType: value.StructuredAddress.FlatOrUnit?.FlatOrUnitType,
      postcode: value.PostCode,
      state: value.StateOrTerritory,
      suburb: value.SuburbOrPlaceOrLocality,
    };

    if (result.houseNumber === undefined) {
      delete result.houseNumber;
    }

    if (result.houseNumberTo === undefined) {
      delete result.houseNumberTo;
    }

    if (result.houseSuffix === undefined || result.houseSuffix === '') {
      delete result.houseSuffix;
    }

    if (
      result.houseNumberToSuffix === undefined ||
      result.houseNumberToSuffix === ''
    ) {
      delete result.houseNumberToSuffix;
    }

    return result;
  });

export type MsatsAddressSchema = z.infer<typeof msatsAddressSchema>;
